import { z } from "zod";

/**
 * Bekleyen İşlemler — Üst Bar panelinin TÜKETTİĞİ projeksiyon (CLOS-102 `[063]`).
 *
 * ### Neden şema bu kartta
 *
 * Kartın kapsamı *"sınırlı kayıt listesi ... ve ilgili sözleşme/değişmez versiyona
 * yönlendirme"* diyor. Kabuk, bir öğeyi çizip doğru yere yönlendirebilmek için onun
 * şeklini bilmek zorunda; şema olmadan kart hiç yazılamazdı.
 *
 * ⚠ **Projeksiyonu ÜRETEN kod bu kartta değil.** Görevleri workflow kartları doğuruyor
 * (CLOS-156 inceleyici görevi, onay/mutabakat/imza kartları, CLOS-181 tam liste). Kart
 * bunu açıkça söylüyor ve `[063]`'ün onlara bağlı olmadığını da. Burada tanımlanan şey
 * kabuğun TÜKETİM sözleşmesi.
 */

/**
 * Görev türleri.
 *
 * ⚠ Anahtarlar UYDURULMADI: yetki kataloğundaki kritik sözleşme aksiyonlarının
 * ta kendisi (`contract.review`, `contract.approve`, `contract.sign`,
 * `contract.reconcile`). Ayrı bir kelime seti üretilseydi aynı kavram iki sözlükte iki
 * adla yaşar ve "bu görev hangi yetkiyi gerektiriyor" sorusunun cevabı bulanıklaşırdı.
 * Bir test bu eşleşmeyi sabitliyor.
 */
export const PENDING_TASK_KINDS = [
  "review",
  "approve",
  "sign",
  "reconcile",
  /** Dinamik iş akışının aşama görevi (Kart 6): onay olabilir, inceleme de olabilir. */
  "workflow_stage",
] as const;
export type PendingTaskKind = (typeof PENDING_TASK_KINDS)[number];

export const PENDING_TASK_KIND_NAMES: Record<PendingTaskKind, string> = {
  review: "İnceleme",
  approve: "Onay",
  sign: "İmza",
  reconcile: "Mutabakat",
  /**
   * ⚠ "Onay" DEĞİL: aşamanın işlem tipi onay da olabilir inceleme de, ve panel hangisi
   * olduğunu aşamanın kendi adından okutuyor.
   */
  workflow_stage: "İş akışı aşaması",
};

export const pendingTaskSchema = z.object({
  id: z.uuid(),
  kind: z.enum(PENDING_TASK_KINDS),
  contractId: z.uuid(),
  /**
   * Değişmez versiyona bağlı görevlerde dolu (imza, onay). Kart: *"gerektiğinde değişmez
   * versiyona yönlendirir"* — hangi versiyonun imzalandığı, sözleşmenin güncel hâlinden
   * bağımsız ve değişmez bir gerçek.
   */
  contractVersionId: z.uuid().nullable(),
  /** Panelde gösterilen sözleşme adı. */
  contractTitle: z.string(),
  assignedAt: z.string(),
  /**
   * Görevin son tarihi — YOKSA `null` (CLOS-1101).
   *
   * ⚠ GECİKMİŞLİK BURADAN TÜRETİLİR, ayrı bir alan olarak YAZILMAZ. Projeksiyona
   * `overdue: true` yazsaydık, son tarih uzatıldığında satırın da güncellenmesi
   * gerekirdi ve okuma yolu bir mutasyona dönerdi. Türetilen değer bayatlamaz.
   *
   * ⚠ Süresi dolan görev listeden DÜŞMÜYOR. Bir süre düşüyordu ve sonucu ölçüldü:
   * `config/contractReview.json` `overdue_1d` eşiğiyle "geciktiniz" bildirimi
   * gönderiyor, kullanıcı panele bakıyor ve görev orada olmuyordu. Gecikmiş iş,
   * görünmesi EN ÇOK gereken iştir.
   */
  dueAt: z.string().nullable(),
  /**
   * İşi TALEP EDEN kişinin adı (CLOS-1112 · R5 §12.3).
   *
   * ⚠ Kartta işin kimden geldiği görünmüyordu: kullanıcı "bu incelemeyi kim istedi?"
   * sorusunu ancak sözleşmeyi açıp akış künyesine bakarak cevaplayabiliyordu. Ad, akış
   * tablolarındaki başlatan kolonundan (`started_by`) okunuyor; projeksiyona ayrıca
   * YAZILMIYOR — kullanıcı adını değiştirirse satır bayatlamasın.
   *
   * ⚠ TABANDA, yalnız tam listede DEĞİL: aynı soru üst bar panelinde ve Ana Sayfa'da da
   * soruluyor. Alan yalnız tam listenin tipinde dursaydı, üç yüzey aynı görevi farklı
   * bilgi setiyle gösterir ve kullanıcı hangisine bakacağını bilemezdi (kriter 6).
   *
   * Başlatan bulunamazsa `null`; ekran o zaman satırı hiç çizmiyor. Bugün dört akış
   * tablosunda `started_by` NOT NULL ve kullanıcı silinmesi kısıtlı (RESTRICT), yani
   * pratikte hep dolu — `null` tip sözleşmesinin savunması, bir ürün durumu değil.
   */
  requestedByName: z.string().nullable(),
  /**
   * SÖZLEŞME BAŞKA BİR ÇALIŞMA ALANINA MI AİT (CLOS-1195).
   *
   * ⚠ Kiracılar arası mutabakat/imza görevinde satır ATANANIN alanında, sözleşme ise
   * karşı tarafın alanında yaşıyor. Bu kişinin kanonik yüzeyi sahibin sözleşme sayfası
   * DEĞİL, paylaşılan sözleşme yüzeyi. Ölçüldü: bağlantı sahibin rotasını gösterdiği için
   * karşı taraf görevi görüyor ama açamıyordu — "Versiyon bulunamadı" alıyordu.
   *
   * ⚠ KİRACI KİMLİĞİ DEĞİL, BİR BAYRAK: ekranın ihtiyacı "hangi rotaya gideyim"; başka
   * bir organizasyonun kimliğini istemciye taşımak, ekranın sormadığı bir bilgiyi
   * yaymak olurdu.
   *
   * ⚠ TABANDA: aynı soru üç yüzeyde de soruluyor (üst bar paneli, Ana Sayfa, tam liste).
   * Yalnız birinde dursaydı diğer ikisi yanlış rotaya götürmeye devam ederdi — bu
   * dosyanın `requestedByName` için verdiği kararın aynısı.
   */
  shared: z.boolean(),
  /**
   * İŞ BENDEN Mİ BEKLENİYOR, YOKSA ÜSTLENEBİLİR MİYİM (CLOS-1243 — Bekleyen İşlemler
   * kanonu §7, §8.3).
   *
   * ```
   * direct    → Benden Beklenenler  — rozet YALNIZ bunu sayar
   * claimable → Üstlenebileceğim    — uygun kişilerden biri yapsa yeter
   * ```
   *
   * ⚠ ÖLÇÜLEN KUSUR: `any` politikalı bir aşama havuzdaki herkese görev satırı açıyor (bu
   * doğru — o küme aktivasyon anının katılımcı anlık görüntüsü). Ama beş uygun kişinin
   * beşi de "sizden 1 iş bekleniyor" görüyordu, oysa işi yalnız BİRİNİN yapması yetiyor.
   * Kanon §7.2 bunu açıkça yasaklıyor: *"Pool action'lar personal badge'i şişirmez."*
   *
   * ⚠ TÜRETİLİYOR, YAZILMIYOR — `dueAt` ve `displayStatus` ile aynı karar. Kural aşamanın
   * kendi anlık görüntüsünde zaten duruyor: `quorum < taskCount` ise iş bir havuz işidir.
   * Projeksiyona `claim_type` diye yazsaydık, görev iptali quorum'u yeniden hesapladığında
   * (`approvals.ts`) satır bayatlar ve okuma yolu bir mutasyona dönerdi.
   *
   * ⚠ Kural `completionPolicy`'ye DEĞİL sayılara bakıyor, çünkü tek uygun kişisi olan bir
   * `any` havuzu gerçekte kişisel iştir: "üstlenebilirsin" demek o işi tek sahibinin
   * rozetinden düşürür ve iş sahipsiz kalır.
   *
   * ⚠ TABANDA: aynı soru üç yüzeyde de soruluyor (üst bar paneli, Ana Sayfa, tam liste).
   * Yalnız birinde dursaydı rozet ile liste çelişirdi — `requestedByName` ve `shared` için
   * verilen kararın aynısı.
   */
  claim: z.enum(["direct", "claimable"]),
});
export type PendingTask = z.infer<typeof pendingTaskSchema>;

/**
 * SON TARİH DURUMU — üç yüzeyin ORTAK cevabı (CLOS-1101).
 *
 * ### ⚠ Neden burada, ekranda değil
 *
 * Bekleyen İşlemler üç yerde çiziliyor: Üst Bar paneli, Ana Sayfa ve tam liste sayfası.
 * "Gecikmiş mi" kuralı üçüne ayrı ayrı yazılsaydı biri güncellenir, diğerleri unutulurdu
 * ve aynı görev iki ekranda iki farklı şey derdi (kriter 6: bilgi tekilleştirilir, satır
 * değil). Kural bir kez burada yaşıyor.
 *
 * ### ⚠ METİN ZORUNLU, renk YETMEZ
 *
 * Durum yalnız renkle anlatılamaz (rapor 4 §21, erişilebilirlik). Bu yüzden fonksiyon bir
 * bayrak değil, okunabilir bir ETİKET döndürüyor.
 *
 * ### ⚠ `now` DIŞARIDAN geliyor
 *
 * `Date.now()` çağırsaydı test edilemez olurdu ve sunucu ile istemci farklı anlar
 * hesaplayabilirdi. Karşılaştırmanın anı çağıranın sorumluluğu.
 */
export function pendingTaskDueState(
  dueAt: string | null,
  now: Date,
): { overdue: boolean; label: string | null } {
  if (!dueAt) return { overdue: false, label: null };
  const son = new Date(dueAt);
  const gecikmis = son.getTime() <= now.getTime();
  const tarih = son.toLocaleDateString("tr-TR", { dateStyle: "medium" });
  return {
    overdue: gecikmis,
    label: gecikmis ? `Gecikti · son tarih ${tarih}` : `Son tarih ${tarih}`,
  };
}

/**
 * İKİ SEMANTİK GRUP — üç yüzeyin ORTAK ayrımı (CLOS-1243 — kanon §7).
 *
 * ### ⚠ Neden burada, ekranda değil
 *
 * `pendingTaskDueState` ile birebir aynı gerekçe: Bekleyen İşlemler üç yerde çiziliyor
 * (Üst Bar paneli, Ana Sayfa, tam liste). Ayrım üçüne ayrı ayrı yazılsaydı biri
 * güncellenir, diğerleri unutulurdu ve rozet ile liste çelişirdi.
 *
 * ### ⚠ ROZET YALNIZ `direct` SAYAR
 *
 * Kanon §7.1: *"Rozet yalnız bu grubu sayar."* §7.2: *"Pool action'lar personal badge'i
 * şişirmez."* Havuz işi listeden DÜŞMÜYOR — yalnız "sizden bekleniyor" demiyor. Düşürseydik
 * uygun kişi işi hiçbir yüzeyde göremez, iş sessizce beklerdi.
 */
export function groupPendingTasks<T extends { claim: PendingTask["claim"] }>(
  tasks: readonly T[],
): { direct: T[]; claimable: T[] } {
  return {
    direct: tasks.filter((g) => g.claim === "direct"),
    claimable: tasks.filter((g) => g.claim === "claimable"),
  };
}

/** İki grubun kullanıcıya görünen adları — kanonun kendi dili (§7.1, §7.2, §37). */
export const PENDING_TASK_GROUP_NAMES = {
  direct: "Benden Beklenenler",
  claimable: "Üstlenebileceğim",
} as const;

/**
 * TAM LİSTE satırı (CLOS-181 `[163]`).
 *
 * ⚠ Panel satırından farkı ÇALIŞMA ALANI bilgisi: tam liste kullanıcının bütün alanlarını
 * kapsıyor ve "bu iş hangi alanda?" sorusu ancak satırın kendisinde cevaplanabilir.
 */
export type PendingTaskListItem = PendingTask & {
  tenantId: string;
  tenantName: string;
  /** ⚠ `requestedByName` artık TABANDA (`pendingTaskSchema`): üç yüzey de aynı alanı okuyor. */
};

/** Tam liste filtreleri — hepsi süzgeç; hiçbiri erişim kapısını gevşetmiyor. */
export const pendingTaskFilterSchema = z.object({
  kinds: z.array(z.enum(PENDING_TASK_KINDS)).optional(),
  tenantIds: z.array(z.uuid()).optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type PendingTaskFilter = z.infer<typeof pendingTaskFilterSchema>;
